<script setup lang="ts">
import UIKit from '@pocketprep/ui-kit'
import type { IAssignmentPayload } from '@/store/assignment/actions'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { daysBetween, getAssignmentStatus } from '@/utils'
import { groupModule } from '@/store/group/module'
import { examModule } from '@/store/exam/module'
import { assignmentModule } from '@/store/assignment/module'
import { organizationModule } from '@/store/organization/module'
import { studentModule } from '@/store/student/module'
import { instructorModule } from '@/store/instructor/module'
import { bundleModule } from '@/store/bundle/module'
import { toastModule } from '@/store/toast/module'
import SubjectsSelector from '@/components/Assignments/SubjectsSelector.vue'
import QuestionsSlider from '@/components/Assignments/QuestionsSlider.vue'
import DifficultySelector from '@/components/Assignments/DifficultySelector.vue'
import AssignmentHead from '@/components/Assignments/AssignmentHead.vue'
import AssignmentFooter from '@/components/Assignments/AssignmentFooter.vue'
import AssignStudents from '@/components/SidePanels/AssignStudents.vue'
import AssignmentDates from '@/components/Assignments/AssignmentDates.vue'
import DeleteAssignmentModal from '@/components/Assignments/DeleteAssignmentModal.vue'
import LegacyBanner from '@/components/Assignments/LegacyBanner.vue'
import type { Teach } from '@pocketprep/types'
import { analyticsModule } from '@/store/analytics/module'
import { licenseModule } from '@/store/license/module'

const {
    Button: PocketButton,
    Input: PocketInput,
    Icon,
    Errors,
    Link: PocketLink,
    Select: PocketSelect,
    Calendar,
    ModalContainer,
    Modal,
} = UIKit

interface IExamSelectItem {
    value?: string | null
    label: string
    type?: 'option' | 'link'
    subtext?: string
}

const router = useRouter()
const route = useRoute()

const isLoading = ref(false)
const selectedExam = ref<IExamSelectItem | null>(null)
const selectedExamVersion = ref<IExamSelectItem | null>(null)
const assignedStudentsIds = ref<string[]>([])
const assignmentAvailableDate = ref<Date | undefined>(undefined)
const assignmentDueDate = ref<Date | undefined>(undefined)
const showAssignStudentsPanel = ref(false)
const onSubmitErrors = ref<string[]>([])
const availableDateErrors = ref<string[]>([])
const dueDateErrors = ref<string[]>([])
const nameErrors = ref<string[]>([])
const difficultiesSelected = ref<('easy' | 'medium' | 'hard')[]>([ 'easy', 'medium', 'hard' ])
const subjectIdsSelected = ref<string[]>([])
const questionCountSelected = ref(15)
const maxQuestionCount = ref(250)
const assignmentName = ref('')
const assignmentStatus = ref<'available' | 'draft' | 'upcoming' | 'closed' | null>(null)
const isLegacyAssignment = ref(false)
const showDeleteModal = ref(false)
const showCancelModal = ref(false)
const originalAssignmentState = ref('')


const currentOrg = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const orgStudents = computed(() => {
    return studentModule.getters.getCurrentOrgStudents()
})

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const orgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const assignments = computed(() => {
    return assignmentModule.getters.getCurrentOrgAssignments()
})

const assignment = computed(() => {
    const assignmentId = route.params.assignmentId
    const storedAssignment = typeof assignmentId === 'string' && assignments.value[assignmentId]

    return storedAssignment || undefined
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentInstructorExams = computed(() => {
    return examModule.getters.getYourOrgExamIds()
})

const selectedOrgExam = computed(() => {
    return Object.values(orgExams.value).find(oe => oe.examMetadata.objectId === selectedExam.value?.value)
})

const isAssignmentAvailable = computed(() => {
    const availableDate = assignment.value && assignment.value.availableDate
    if (!availableDate) {
        return false
    }
    const startDate = availableDate instanceof Date
        ? availableDate
        : new Date(availableDate.iso)

    const today = new Date()

    if (today.getTime() > startDate.getTime()) {
        return true
    } else {
        return false
    }
})

const selectedExamQuestionsPayload = computed(() => {
    const organizationId = currentOrg.value?.objectId
    if (!organizationId) {
        return null
    }
    const selectedOrDefaultExamMetadataId = selectedExamVersion.value?.value || selectedExam.value?.value
        || mappedCurrentInstructorExams.value[0]?.value || ''
    const compositeKey = examMetadata.value[selectedOrDefaultExamMetadataId]?.compositeKey || ''
    return {
        compositeKey,
        organizationId,
        examMetadataId: selectedOrDefaultExamMetadataId,
    }
})

const subjects = computed(() => {
    const currentExamSubjects = examModule.getters.getCurrentExamSubjects()
    return Object.values(currentExamSubjects)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
})

const mappedCurrentInstructorExams = computed((): IExamSelectItem[] => {
    const filteredOrgExams = Object.values(orgExams.value).filter(orgExam => {
        const orgExamMetadata = examMetadata.value[orgExam.examMetadata?.objectId]
        return orgExamMetadata
            && (currentOrgInstructor.value?.isAdmin || currentInstructorExams.value.includes(orgExam.objectId))
            && !examModule.getters.getIsExamMetadataRemoved(orgExamMetadata)
    })
    return filteredOrgExams.filter((exam, index, self) => {
        return index === self.findIndex(e => exam.displayName === e.displayName)
    })
        .map(exam => ({
            value: exam.examMetadata.objectId,
            label: exam.displayName,
        }))
})

const examVersionsInCurrentExam = computed(() => {
    const selectedExamMetadataId = selectedExam.value?.value
    const availableExamVersions = selectedExamMetadataId
        ? examModule.getters.getExamVersionsForExamMetadataId(selectedExamMetadataId)
        : []

    return availableExamVersions
})

const mappedExamVersions = computed(() => {
    if (!examVersionsInCurrentExam.value.length) {
        return []
    } else {
        return examVersionsInCurrentExam.value.map(majorExamVersion => ({
            value: majorExamVersion.objectId,
            label: majorExamVersion.releaseInfo.name,
        }))
    }
})

const questionDifficultyCountsBySubject = computed(() => {
    return assignmentModule.getters.getCurrentOrgDifficultyCounts()
})

const questionDifficultyCounts = computed(() => {
    return subjectIdsSelected.value.reduce((acc, subjectId) => {
        const subjectDifficulties = questionDifficultyCountsBySubject.value[subjectId]
        if (!subjectDifficulties) {
            return acc
        }

        acc.easy += subjectDifficulties.easy
        acc.medium += subjectDifficulties.medium
        acc.hard += subjectDifficulties.hard
        return acc
    }, { easy: 0, medium: 0, hard: 0 })
})

const userIdOrgStudentLib = computed(() => {
    return Object.values(orgStudents.value).reduce<{ [userId: string]: Teach.Class.OrgStudentJSON }>((acc, s) => {
        if (!s.user || s.isArchived) {
            return acc
        }
        acc[s.user.objectId] = s

        return acc
    }, {}) || {}
})

const studentsUnavailableForUnassign = computed(() => {
    // if assignment is already available or is legacy, we shouldn't be able to unassign any students
    if ((isAssignmentAvailable.value || isLegacyAssignment.value) && assignment.value?.assignedOrgStudents) {
        return assignment.value?.assignedOrgStudents.map(student => student.objectId)
    } else {
        return []
    }
})

const assignmentStudentCompletedStats = computed(() => {
    if (!assignment.value) {
        return null
    }
    const studentsCompletedAssignmentCount = Object.entries(assignment.value.attempts || {})
        .reduce((completedCount, [ userId ]) => {
            const orgStudent = userIdOrgStudentLib.value[userId]
            if (!orgStudent || orgStudent.isArchived || !orgStudent.user) {
                return completedCount
            }

            completedCount++
            return completedCount
        }, 0)
    const studentsAssignedCount = assignment.value.assignedOrgStudents?.length || 0
    return {
        studentsCompletedCount: studentsCompletedAssignmentCount,
        studentsNotCompletedCount: studentsAssignedCount - studentsCompletedAssignmentCount,
    }
})

const isAssignmentEdited = computed(() => {
    const assignmentState = {
        exam: selectedExam.value,
        examVersion: selectedExamVersion.value,
        assignedStudents: assignedStudentsIds.value,
        config: {
            questionCount: questionCountSelected.value,
            subjectIds: subjectIdsSelected.value,
            difficulties: difficultiesSelected.value,
        },
        availableDate: assignmentAvailableDate.value,
        dueDate: assignmentDueDate.value,
        name: assignmentName.value,
    }

    return JSON.stringify(assignmentState) !== originalAssignmentState.value
})

const isPublishingDisabled = computed(() => {
    return !assignmentAvailableDate.value
        || !assignmentDueDate.value
        || availableDateErrors.value.length
        || dueDateErrors.value.length
        || nameErrors.value.length
        || !assignmentName.value.length
        || (!isAssignmentEdited.value && assignmentStatus.value !== 'draft')
        || !assignedStudentsIds.value.length
        || !questionCountSelected.value
})

const validateName = () => {
    nameErrors.value = []
    if (assignmentName.value.length >= 56) {
        nameErrors.value.push('Name must be shorter than 56 characters.')
    }
}

const validateDates = () => {
    availableDateErrors.value = []
    dueDateErrors.value = []
    const availableDate = assignmentAvailableDate.value
    const dueDate = assignmentDueDate.value
    if (availableDate && dueDate) {
        if (availableDate?.getTime() > dueDate?.getTime()) {
            dueDateErrors.value.push('Due Date must be after the Available Date.')
        }
    }
    if (!assignment.value || assignment.value.status === 'draft') {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        if (availableDate && availableDate.getTime() < today.getTime()) {
            availableDateErrors.value.push('Date cannot be in the past.')
        }
        if (dueDate && dueDate.getTime() < today.getTime()) {
            dueDateErrors.value.push('Date cannot be in the past.')
        }
    }
}

const formatAndSetDate = (calendar: 'available' | 'due', pickedDate?: Date | null) => {
    if (calendar === 'available') {
        if (pickedDate) {
            const newAvailableDate = new Date(pickedDate)
            newAvailableDate.setHours(0, 0, 0, 0)
            assignmentAvailableDate.value = newAvailableDate
        } else {
            assignmentAvailableDate.value = undefined
        }
    } else {
        if (pickedDate) {
            const newDueDate = new Date(pickedDate)
            newDueDate.setDate(pickedDate.getDate() + 1)
            newDueDate.setMilliseconds(newDueDate.getMilliseconds() - 1)
            assignmentDueDate.value = newDueDate
        } else {
            assignmentDueDate.value = undefined
        }
    }

    validateDates()
}

const subjectSelectionChanged = (subjectIds: string[]) => {
    subjectIdsSelected.value = subjectIds
}

const calculateMaxQuestionCount = () => {
    const availableQuestions = difficultiesSelected.value.reduce((acc, d) => {
        acc += questionDifficultyCounts.value[d] || 0
        return acc
    }, 0)
    maxQuestionCount.value = availableQuestions > 250 ? 250 : availableQuestions
}

const studentSelectionChanged = (updatedStudentIds: string[]) => {
    assignedStudentsIds.value = updatedStudentIds
    showAssignStudentsPanel.value = false
}

const updateQuestionsForSelectedExam = async () => {
    if (!selectedExamQuestionsPayload.value) {
        return
    }
    isLoading.value = true
    const selectedExamId = selectedExamVersion.value?.value || selectedExam.value?.value
    await examModule.actions.fetchQuestionsByExam(selectedExamQuestionsPayload.value, true)
    if (selectedExamId) {
        await assignmentModule.actions.fetchQuestionDifficultyCountsForExamId(selectedExamId, true)
    }
    if (!assignment.value) {
        subjectIdsSelected.value = subjects.value.map(subj => subj.objectId)
    }
    isLoading.value = false
}

const storeOriginalAssignmentState = () => {
    const originalDataState = {
        exam: selectedExam.value,
        examVersion: selectedExamVersion.value,
        assignedStudents: assignedStudentsIds.value,
        config: {
            questionCount: questionCountSelected.value,
            subjectIds: subjectIdsSelected.value,
            difficulties: difficultiesSelected.value,
        },
        availableDate: assignmentAvailableDate.value,
        dueDate: assignmentDueDate.value,
        name: assignmentName.value,
    }

    originalAssignmentState.value = JSON.stringify(originalDataState)
}

const handleCancel = () => {
    if (isAssignmentEdited.value) {
        showCancelModal.value = true
    } else {
        router.back()
    }
}

const deleteAssignment = async () => {
    isLoading.value = true
    const assignmentId = assignment.value?.objectId
    if (!assignmentId) {
        return
    }
    try {
        await assignmentModule.actions.deleteAssignment({ assignmentId })
        await router.push({ name: 'assignments' })
        toastModule.actions.displayToast({
            title: 'Assignment deleted.',
        })
        if (currentOrg.value) {
            analyticsModule.actions.amplitudeTrack('Assignment Deleted', {
                organizationId: currentOrg.value.objectId,
                hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
                examGuid: examMetadata.value[assignment.value.examMetadata.objectId]?.examGuid,
                bundleId: bundleModule.getters.getExamBundle(assignment.value.examMetadata.objectId)?.objectId || '',
                userIsOwnerOfAssignment: assignments.value[assignmentId]?.creator
                    .objectId === currentOrgInstructor.value?.user.objectId,
                assignmentStatus: assignment.value.status === 'draft'
                    ? 'draft'
                    : getAssignmentStatus({
                        availableDate: assignment.value.availableDate?.iso,
                        dueDate: assignment.value.dueDate?.iso,
                    }),
            })
        }
    } catch (err) {
        onSubmitErrors.value = []
        onSubmitErrors.value.push('There was a problem deleting this assignment. Please try again.')

        showDeleteModal.value = false
    }
    isLoading.value = false
}

const upsertAssignment = async (options?: {
    isSavingAsDraft: boolean
}) => {
    isLoading.value = true
    const examMetadataId = selectedExamVersion.value?.value || selectedExam.value?.value

    if (!examMetadataId || !currentOrg.value) {
        onSubmitErrors.value.push('There was a problem publishing this assignment. Please try again.')
        return
    }

    const assignmentId = assignment.value?.objectId
    const assignedOrgStudentIds = assignedStudentsIds.value
    const availableDate = assignmentAvailableDate.value
        ? new Date(assignmentAvailableDate.value).toISOString()
        : undefined
    const dueDate = assignmentDueDate.value
        ? new Date(assignmentDueDate.value).toISOString()
        : undefined
    const status = options?.isSavingAsDraft ? 'draft' : 'active'
    const name = assignmentName.value
    const config = {
        difficulties: [ ...difficultiesSelected.value ],
        questionCount: questionCountSelected.value,
        subjectIds: subjectIdsSelected.value,
    }

    const assignmentPayload: IAssignmentPayload = {
        assignmentId,
        name,
        examMetadataId,
        assignedOrgStudentIds,
        availableDate,
        dueDate,
        status,
        config,
    }
    try {
        const createdAssignment = await assignmentModule.actions.upsertAssignment(assignmentPayload)
        const createOrUpdateEventPayload = createdAssignment && {
            organizationId: currentOrg.value.objectId,
            hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
            examGuid: examMetadata.value[createdAssignment.examMetadata.objectId]?.examGuid,
            bundleId: bundleModule.getters.getExamBundle(createdAssignment.examMetadata.objectId)?.objectId || '',
            organizationName: currentOrg.value.name,
            assignmentName: createdAssignment.name,
            assignmentQuestionCount: createdAssignment.config.questionCount,
            assignmentDifficulties: JSON.stringify(createdAssignment.config.difficulties),
            assignmentSubjectCount: createdAssignment.config.subjectIds?.length || 0,
            assignmentSubjects: JSON.stringify(createdAssignment.config.subjectIds),
            assignmentStudentCount: createdAssignment.assignedOrgStudents?.length || 0,
            assignmentDaysUntilAvailable: createdAssignment.availableDate
                ? daysBetween({
                    startDate: new Date(),
                    endDate: createdAssignment.availableDate?.iso,
                })
                : 0,
            assignmentDaysActive: createdAssignment.availableDate && createdAssignment.dueDate
                ? daysBetween({
                    startDate: createdAssignment.availableDate?.iso,
                    endDate: createdAssignment.dueDate?.iso,
                })
                : 0,
            assignmentStatus: createdAssignment.status === 'draft'
                ? 'draft'
                : getAssignmentStatus({
                    availableDate: createdAssignment.availableDate?.iso,
                    dueDate: createdAssignment.dueDate?.iso,
                }),
        }
        if (assignment.value) {
            if (status === 'draft') {
                toastModule.actions.displayToast({
                    title: 'Draft saved.',
                })
            } else {
                toastModule.actions.displayToast({
                    title: 'Assignment updated.',
                })
            }

            if (createOrUpdateEventPayload) {
                analyticsModule.actions.amplitudeTrack('Assignment Updated', createOrUpdateEventPayload)
            }
        } else {
            if (status === 'draft') {
                toastModule.actions.displayToast({
                    title: 'Assignment saved as draft.',
                })
            } else {
                // save new assignment in state so assignments list can fire up success modal
                assignmentModule.state.createdAssignment = createdAssignment
            }

            if (createOrUpdateEventPayload) {
                analyticsModule.actions.amplitudeTrack('Assignment Created', createOrUpdateEventPayload)
            }
        }

        await router.push({ name: 'assignments' })
    } catch (err) {
        onSubmitErrors.value = []
        if (assignment.value) {
            if (status === 'draft') {
                onSubmitErrors.value.push('There was a problem saving this draft. Please try again.')
            } else {
                onSubmitErrors.value.push('There was a problem updating this assignment. Please try again.')
            }
        } else {
            if (status === 'draft') {
                onSubmitErrors.value.push('There was a problem saving as a draft. Please try again.')
            } else {
                onSubmitErrors.value.push('There was a problem publishing this assignment. Please try again.')
            }
        }
    }
    isLoading.value = false
}

watch(selectedExam, () => {
    if (mappedExamVersions.value.length > 1) {
        const firstAvailableVersion = mappedExamVersions.value[0]
        if (!assignment.value && firstAvailableVersion) {
            selectedExamVersion.value = firstAvailableVersion
        }
    } else {
        selectedExamVersion.value = null
    }
})

watch([
    () => selectedExamVersion,
    () => selectedExam,
], updateQuestionsForSelectedExam, { deep: true })

watch([
    () => difficultiesSelected.value,
    () => questionDifficultyCounts.value,
], calculateMaxQuestionCount, { deep: true })

onMounted(async () => {
    isLoading.value = true
    await Promise.all([
        bundleModule.actions.fetchBundles(),
        examModule.actions.fetchExamMetadata(),
        instructorModule.actions.fetchCurrentOrgInstructor(),
        examModule.actions.fetchCurrentOrgExams(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        groupModule.actions.fetchCurrentOrgGroups(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        assignmentModule.actions.fetchAssignments(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])

    // fill out the form with an existing assignment
    if (assignment.value) {
        const storedAssignment = assignment.value

        const orgExamDisplayName = Object.values(orgExams.value).find(oe =>
            oe.examMetadata.objectId === storedAssignment.examMetadata.objectId)?.displayName || ''
        selectedExam.value = {
            value: storedAssignment.examMetadata.objectId,
            label: orgExamDisplayName,
        }
        assignedStudentsIds.value = storedAssignment.assignedOrgStudents?.length
            ? [ ...storedAssignment.assignedOrgStudents.map(aos => aos.objectId) ]
            : []
        if (mappedExamVersions.value.length > 1) {
            const examVersion = mappedExamVersions.value.find(em => em.value === storedAssignment.examMetadata.objectId)
            selectedExamVersion.value = examVersion || null
        }
        if (storedAssignment.config.difficulties) {
            difficultiesSelected.value = storedAssignment.config.difficulties
        }
        questionCountSelected.value = storedAssignment.questionSerials.length
        subjectIdsSelected.value = storedAssignment.config.subjectIds || []
        assignmentAvailableDate.value = storedAssignment.availableDate
            ? new Date(storedAssignment.availableDate.iso)
            : undefined
        assignmentDueDate.value = storedAssignment.dueDate ? new Date(storedAssignment.dueDate.iso) : undefined
        assignmentName.value = storedAssignment.name
        if (!assignment.value.availableDate && !storedAssignment.dueDate && storedAssignment.status !== 'draft') {
            isLegacyAssignment.value = true
        }
        const availableDate = storedAssignment.availableDate?.iso
        const dueDate = storedAssignment.dueDate?.iso
        assignmentStatus.value = isLegacyAssignment.value
            ? 'available'
            : storedAssignment.status === 'draft'
                ? 'draft'
                : getAssignmentStatus({ availableDate, dueDate })
    } else {
        const firstAvailableInstructorExam = mappedCurrentInstructorExams.value[0]
        if (firstAvailableInstructorExam) {
            selectedExam.value = firstAvailableInstructorExam
        }
        const firstAvailableExamVersion = mappedExamVersions.value[0]
        if (examVersionsInCurrentExam.value.length > 1 && firstAvailableExamVersion) {
            selectedExamVersion.value = firstAvailableExamVersion
        }
    }

    validateDates()
    validateName()
    await updateQuestionsForSelectedExam()

    await nextTick()
    storeOriginalAssignmentState()

    isLoading.value = false
})
</script>

<template>
    <DeleteAssignmentModal
        v-if="showDeleteModal && assignmentStudentCompletedStats"
        :assignmentStudentCompletedStats="assignmentStudentCompletedStats"
        @close="showDeleteModal = false"
        @delete="deleteAssignment"
    />
    <ModalContainer v-if="showCancelModal" @close="showCancelModal = false">
        <Modal :showCloseButton="true" @close="showCancelModal = false">
            <div class="assignment-form__cancel-modal-container">
                <div class="assignment-form__cancel-modal-head">
                    {{ assignment ? 'Cancel Updates?' : 'Cancel Assignment?' }}
                </div>
                <div class="assignment-form__cancel-modal-text">
                    {{ assignment
                        ? 'You will lose the updates you\'ve made to this assignment.'
                        : 'You will lose your progress.' }}
                </div>
                <div class="assignment-form__cancel-modal-buttons">
                    <PocketButton
                        type="secondary"
                        @click="showCancelModal = false"
                    >
                        Keep Editing
                    </PocketButton>
                    <PocketButton @click="router.back()">
                        {{ assignment ? 'Cancel Updates' : 'Cancel Assignment' }}
                    </PocketButton>
                </div>
            </div>
        </Modal>
    </ModalContainer>
    <div class="assignment-form__background">
        <AssignmentHead v-if="!isLoading">
            <template #right>
                <AssignmentDates
                    v-if="assignment && assignmentStatus"
                    :isDark="true"
                    :status="assignmentStatus"
                    :availableDate="assignment.availableDate?.iso"
                    :dueDate="assignment.dueDate?.iso"
                    :isLegacy="isLegacyAssignment"
                    class="assignment-form__date-status"
                />
            </template>
        </AssignmentHead>
        <div class="assignment-form" v-if="!isLoading">
            <AssignStudents
                v-if="showAssignStudentsPanel && selectedOrgExam"
                :orgExamId="selectedOrgExam.objectId"
                :examGuid="examMetadata[selectedOrgExam.examMetadata.objectId]?.examGuid || ''"
                :assignedStudentsIds="assignedStudentsIds"
                :disabledCheckboxStudents="studentsUnavailableForUnassign"
                :autoFilterByInstructor="!assignment"
                @studentsUpdated="studentSelectionChanged"
                @close="showAssignStudentsPanel = !showAssignStudentsPanel"
            />
            <LegacyBanner v-if="isLegacyAssignment" />
            <Errors :errors="onSubmitErrors" v-if="onSubmitErrors.length" />
            <div class="assignment-form__title-header">
                <div class="assignment-form__title">
                    {{ assignment ? 'Edit Assignment' : 'Create Assignment' }}
                    <Icon
                        v-if="!assignment"
                        type="highlight"
                        class="assignment-form__highlight"
                    />
                </div>
                <AssignmentDates
                    v-if="assignment && assignmentStatus"
                    :status="assignmentStatus"
                    :availableDate="assignment.availableDate?.iso"
                    :dueDate="assignment.dueDate?.iso"
                    :isLegacy="isLegacyAssignment"
                    class="assignment-form__date-status assignment-form__date-status--light"
                />
            </div>
            <PocketSelect
                v-model="selectedExam"
                :data="mappedCurrentInstructorExams"
                label="Exam"
                :disabled="!!assignment"
            />
            <PocketSelect
                v-if="mappedExamVersions.length > 1"
                v-model="selectedExamVersion"
                :data="mappedExamVersions"
                :disabled="!!assignment"
                label="Exam Version"
                class="assignment-form__exam-versions-select"
            />
            <div class="assignment-form__student-select-container">
                <div class="assignment-form__student-select-help">
                    Select which {{ selectedExam?.label || '' }} students you would like to assign this quiz to.
                </div>
                <div class="assignment-form__selected-students">
                    <div class="assignment-form__student-count">
                        {{ `${assignedStudentsIds.length} students selected` }}
                    </div>
                    <PocketLink
                        class="assignment-form__add-students-link"
                        @click="showAssignStudentsPanel = !showAssignStudentsPanel"
                    >
                        <Icon
                            :type="assignedStudentsIds.length ? 'person' : 'add'"
                            class="assignment-form__add-students-icon"
                        />
                        {{ assignedStudentsIds.length
                            ? 'Update Assigned Students'
                            : 'Add Students to Assignment' }}
                    </PocketLink>
                </div>
            </div>
            <div class="assignment-form__assignment-details">
                <div class="assignment-form__assignment-details-help">
                    Customize the assignment details
                </div>
                <SubjectsSelector
                    v-if="subjects.length"
                    :subjects="subjects"
                    :initialShowSubjects="false"
                    :initialSelectedSubjects="subjectIdsSelected"
                    :disabled="assignmentStatus !== 'draft' && (isAssignmentAvailable || isLegacyAssignment)"
                    @selectedSubjects="subjectSelectionChanged"
                    class="assignment-form__subjects-selector"
                />
                <DifficultySelector
                    v-model="difficultiesSelected"
                    :questionDifficultyCounts="questionDifficultyCounts"
                    :disabled="assignmentStatus !== 'draft' && (isAssignmentAvailable || isLegacyAssignment)"
                    class="assignment-form__difficulty-selector"
                />
                <QuestionsSlider
                    class="assignment-form__question-count"
                    v-model="questionCountSelected"
                    :disabled="assignmentStatus !== 'draft' && (isAssignmentAvailable || isLegacyAssignment)"
                    :maxQuizSize="maxQuestionCount"
                    :minQuizSize="maxQuestionCount ? 1 : 0"
                />
                <Calendar
                    ref="availableDatePickerRef"
                    :modelValue="assignmentAvailableDate"
                    :error="!!availableDateErrors.length"
                    :disabled="(assignmentStatus === 'available' || assignmentStatus === 'closed')
                        && !isLegacyAssignment"
                    @update:modelValue="formatAndSetDate('available', $event)"
                    label="Available Date"
                    labelSubtext="When assignment will be available to take and visible to students in the app."
                    :key="String(assignmentAvailableDate)"
                    class="assignment-form__available-date-calendar"
                />
                <Errors
                    v-if="availableDateErrors.length"
                    :errors="availableDateErrors"
                    class="assignment-form__available-date-errors"
                />
                <Calendar
                    ref="dueDatePicker"
                    :modelValue="assignmentDueDate"
                    :error="!!dueDateErrors.length"
                    @update:modelValue="formatAndSetDate('due', $event)"
                    label="Due Date"
                    labelSubtext="After 11:59pm on this date, assignment will no longer be available to students."
                    :key="String(assignmentDueDate)"
                    class="assignment-form__due-date-calendar"
                />
                <Errors
                    v-for="(error, index) in dueDateErrors"
                    :key="error"
                    :errors="[dueDateErrors[index] as string]"
                    class="assignment-form__due-date-errors"
                />
                <PocketInput
                    v-model="assignmentName"
                    :error="!!nameErrors.length"
                    :disabled="assignmentStatus !== 'draft' && (isAssignmentAvailable || isLegacyAssignment)"
                    @update:modelValue="validateName"
                    label="Assignment Name"
                    class="assignment-form__assignment-name-input"
                />
                <Errors
                    v-if="nameErrors.length"
                    :errors="nameErrors"
                    class="assignment-form__assignment-name-errors"
                />
            </div>
            <AssignmentFooter>
                <template #left>
                    <PocketButton
                        v-if="assignment"
                        type="secondary"
                        class="assignment-form__delete-btn"
                        @click="showDeleteModal = true"
                    >
                        Delete Assignment
                    </PocketButton>
                </template>
                <template #center>
                    <PocketButton
                        class="assignment-form__save-as-draft-btn"
                        v-if="!assignment || assignmentStatus === 'draft'"
                        type="secondary"
                        @click="upsertAssignment({isSavingAsDraft: true})"
                    >
                        <Icon type="preview" class="assignment-form__save-as-draft-icon" />
                        <span>
                            Save as Draft
                        </span>
                    </PocketButton>
                </template>
                <template #right>
                    <PocketButton
                        class="assignment-form__cancel-btn"
                        type="secondary"
                        @click="handleCancel"
                    >
                        Cancel
                    </PocketButton>
                    <PocketButton
                        :disabled="!!isPublishingDisabled"
                        class="assignment-form__publish-btn"
                        @click="upsertAssignment({isSavingAsDraft: false})"
                    >
                        {{ assignment && assignment.status !== 'draft' ? 'Update Assignment' : 'Publish Assignment' }}
                    </PocketButton>
                </template>
            </AssignmentFooter>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.assignment-form {
    max-width: 496px;
    padding: 32px 44px 80px 44px;
    margin: 54px auto 80px;

    &__background {
        height: 100vh;
        overflow: scroll;
        background-color: $gray-background;
    }

    &__title-header {
        display: flex;
        margin-top: 28px;
        margin-bottom: 56px;
        align-items: center;
        justify-content: space-between;
    }

    &__title {
        font-size: 26px;
        line-height: 34px;
        font-weight: 600;
        position: relative;
    }

    &__highlight {
        position: absolute;
        left: -8px;
        bottom: -15px;
        width: calc(100% + 16px);
        color: $pearl;
    }

    &__exam-versions-select {
        margin-top: 24px;
        margin-bottom: 40px;
    }

    &__student-select-container {
        padding: 0 12px;
    }

    &__student-select-help {
        margin-top: 40px;
        font-weight: 600;
        font-size: 16px;
        line-height: 22px;
        margin-bottom: 14px;
    }

    &__selected-students {
        display: flex;
        height: 20px;
        margin-bottom: 40px;
    }

    &__student-count {
        font-size: 15px;
        line-height: 20px;
        padding-right: 24px;
    }

    &__add-students-link {
        display: flex;
        align-items: center;

        svg {
            position: relative;
            bottom: 1px;
        }
    }

    &__add-students-icon {
        height: 18px;
        width: 18px;
        margin-right: 4px;
    }

    &__assignment-details {
        padding: 0 2px;
    }

    &__assignment-details-help {
        padding-left: 14px;
        font-size: 16px;
        line-height: 22px;
        font-weight: 600;
        margin-bottom: 14px;
    }

    &__subjects-selector {
        margin-bottom: 16px;
    }

    &__difficulty-selector {
        margin-bottom: 16px;
    }

    &__available-date-calendar {
        margin-top: 40px;
    }

    &__due-date-calendar {
        margin-top: 24px;
    }

    &__available-date-errors,
    &__due-date-errors,
    &__assignment-name-errors {
        margin-top: 12px;
    }

    &__assignment-name-input {
        margin-top: 24px;
    }

    &__save-as-draft-btn {

        :deep(.uikit-btn__content) {
            display: flex;
            align-items: center;
        }
    }

    &__publish-btn {
        margin-right: 30px;
        margin-left: 8px;
    }

    &__save-as-draft-icon {
        margin-right: 8px;
    }

    &__date-status {
        margin-right: 44px;

        &--light {
            margin-right: 0;
            color: $ash;
        }
    }

    &__cancel-modal-container {
        padding: 36px 40px 24px;
        text-align: center;
    }

    &__cancel-modal-head {
        font-size: 18px;
        line-height: 25px;
        letter-spacing: 0.3px;
        font-weight: 600;
        margin-bottom: 12px;
    }

    &__cancel-modal-text {
        color: $ash;
        font-size: 15px;
        line-height: 20px;
        margin-bottom: 32px;
    }

    &__delete-btn {
        margin-left: 30px;

        :deep(.uikit-btn__content) {
            font-weight: 600;
            color: $pepper;
        }
    }

    :deep(.uikit-errors__error) {
        font-size: 15px;
    }
}
</style>
